import { query, where } from "firebase/firestore";
import { Repository } from "src/firebase/firestore/repository";
import {
  JobApplication,
  JobCandidate,
  JobHelpRequest,
  JobSearchRequest,
  PublicJob,
  RecruiterCvUpload,
} from "../types/recruiter.entity";

/**
 * The recruiter's records are readable here only because `firestore.rules`
 * grants ADMIN a read on them (taaly_backend, `feat/recruiter-admin-access`,
 * and `recruiter_cv_uploads` from `fix/recruiter-cv-per-conversation`).
 * EMPLOYEE is deliberately not granted: a candidate row reveals migration
 * status. Every repository below is read-only in practice — the rules deny
 * all writes, and the recruiter is the one writer.
 */

class JobApplicationsRepository extends Repository<JobApplication> {
  constructor() {
    super("job_applications");
  }
}

class JobHelpRequestsRepository extends Repository<JobHelpRequest> {
  constructor() {
    super("job_help_requests");
  }
}

class JobSearchRequestsRepository extends Repository<JobSearchRequest> {
  constructor() {
    super("job_search_requests");
  }
}

class JobCandidatesRepository extends Repository<JobCandidate> {
  constructor() {
    super("job_candidates");
  }
}

class RecruiterCvUploadsRepository extends Repository<RecruiterCvUpload> {
  constructor() {
    super("recruiter_cv_uploads");
  }

  /**
   * Every CV offered in one conversation, newest first.
   *
   * One equality filter and a client-side sort rather than
   * `orderBy("createdAt")` on top of it: that combination needs a composite
   * index, and a conversation holds one upload, or two when a learner replaces
   * the file before finishing. Sorting two rows in memory is not worth an index
   * and a deploy.
   *
   * `userId` is checked by the caller, not added here as a second filter, so
   * this stays a single-field query and the ownership check stays next to the
   * path check it belongs with.
   */
  async findByConversation(conversationId: string): Promise<RecruiterCvUpload[]> {
    const rows = await this.findBy(query(this.collection, where("conversationId", "==", conversationId)));

    return rows.sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
  }
}

class PublicJobsRepository extends Repository<PublicJob> {
  constructor() {
    super("public_jobs");
  }
}

export const jobApplicationsRepository = new JobApplicationsRepository();
export const jobHelpRequestsRepository = new JobHelpRequestsRepository();
export const jobSearchRequestsRepository = new JobSearchRequestsRepository();
export const jobCandidatesRepository = new JobCandidatesRepository();
export const recruiterCvUploadsRepository = new RecruiterCvUploadsRepository();
export const publicJobsRepository = new PublicJobsRepository();
